/**
 * room-planner.js — Home Cinema Supply
 * Phase 2: top-down 2D room planner (SVG). Vanilla JS, no dependencies.
 *
 * Coordinate system: SVG user units ARE inches.
 *   x — 0 at the left wall, increasing right
 *   y — 0 at the SCREEN wall, increasing toward the back wall
 * A row's `y` is the position of its SEAT BACK, matching room-geometry.js.
 *
 * Shared maths lives in room-geometry.js; this file owns layout and input.
 */
(function () {
  'use strict';

  var G = window.RoomGeometry;
  if (!G) return;

  /* =======================================================================
   * TUNABLE CONSTANTS — see also room-geometry.js DEFAULTS.
   * ======================================================================= */
  var C = {
    // Generic row sizing, used because the planner is product-agnostic.
    SEAT_WIDTH_IN: 30, // per seat, including its share of the armrests
    ROW_END_ALLOWANCE_IN: 6, // the extra outer arm
    ROW_DEPTH_UPRIGHT_IN: 40,
    ROW_DEPTH_RECLINED_IN: 68,
    ROW_WALL_CLEARANCE_IN: 6,

    SNAP_TOLERANCE_IN: 6, // how close before a drag snaps
    MIN_ROOM_IN: 96,
    MAX_ROOM_IN: 720,

    SEAT_OPTIONS: [2, 3, 4, 5, 6]
  };

  function rowWidthFor(seats) {
    return seats * C.SEAT_WIDTH_IN + C.ROW_END_ALLOWANCE_IN;
  }

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var uidCounter = 0;

  function el(name, attrs, parent) {
    var node = document.createElementNS(SVG_NS, name);
    for (var k in attrs) {
      if (attrs[k] !== null && attrs[k] !== undefined) node.setAttribute(k, attrs[k]);
    }
    if (parent) parent.appendChild(node);
    return node;
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  var ft = function (n) { return G.formatInches(n); };
  var ftc = function (n) { return G.formatInches(n, { compact: true }); };
  var clamp = function (v, lo, hi) { return Math.min(hi, Math.max(lo, v)); };

  /* =======================================================================
   * Planner instance
   * ======================================================================= */
  function Planner(root) {
    this.root = root;
    this.uid = 'rp' + ++uidCounter;
    this.defaults = JSON.parse(root.getAttribute('data-planner-defaults') || '{}');

    this.state = {
      room: {
        widthIn: this.defaults.widthIn || 168,
        lengthIn: this.defaults.lengthIn || 240,
        screenDiagonalIn: this.defaults.screenIn || 120,
        ceilingHeightIn: this.defaults.ceilingIn || 96
      },
      rows: [],
      riserHeightIn: G.DEFAULTS.RISER_HEIGHT_DEFAULT_IN,
      selectedId: null
    };

    this.drag = null;
    this.cacheNodes();
    this.bind();
    this.addRow(3);
    this.render();
  }

  Planner.prototype.cacheNodes = function () {
    var r = this.root;
    this.svg = r.querySelector('[data-rp-svg]');
    this.warningsEl = r.querySelector('[data-rp-warnings]');
    this.summaryEl = r.querySelector('[data-rp-summary]');
    this.rowListEl = r.querySelector('[data-rp-rows]');
    this.inputs = {};
    Array.prototype.forEach.call(r.querySelectorAll('[data-rp-input]'), function (i) {
      this.inputs[i.getAttribute('data-rp-input')] = i;
    }.bind(this));
    // Seed the controls from state.
    if (this.inputs.width) this.inputs.width.value = ftc(this.state.room.widthIn);
    if (this.inputs.length) this.inputs.length.value = ftc(this.state.room.lengthIn);
    if (this.inputs.screen) this.inputs.screen.value = this.state.room.screenDiagonalIn;
    if (this.inputs.ceiling) this.inputs.ceiling.value = ftc(this.state.room.ceilingHeightIn);
  };

  Planner.prototype.bind = function () {
    var self = this;

    Object.keys(this.inputs).forEach(function (key) {
      self.inputs[key].addEventListener('change', function () { self.readRoomInputs(); });
      self.inputs[key].addEventListener('blur', function () { self.readRoomInputs(); });
    });

    this.root.addEventListener('click', function (e) {
      var addBtn = e.target.closest('[data-rp-add]');
      if (addBtn) { self.addRow(Number(addBtn.getAttribute('data-rp-add'))); self.render(); return; }
      var rm = e.target.closest('[data-rp-remove]');
      if (rm) { self.removeRow(rm.getAttribute('data-rp-remove')); self.render(); return; }
      var em = e.target.closest('[data-rp-email]');
      if (em) { self.emailLayout(); return; }
      var rs = e.target.closest('[data-rp-reset]');
      if (rs) { self.reset(); return; }
    });

    // Keyboard nudging for the selected row — the planner must not require a
    // mouse to be usable.
    this.root.addEventListener('keydown', function (e) {
      var g = e.target.closest('[data-row-id]');
      if (!g) return;
      var row = self.getRow(g.getAttribute('data-row-id'));
      if (!row) return;
      var step = e.shiftKey ? 12 : 2;
      var handled = true;
      if (e.key === 'ArrowUp') row.y -= step;
      else if (e.key === 'ArrowDown') row.y += step;
      else if (e.key === 'ArrowLeft') row.cx -= step;
      else if (e.key === 'ArrowRight') row.cx += step;
      else if (e.key === 'Delete' || e.key === 'Backspace') { self.removeRow(row.id); }
      else handled = false;
      if (handled) {
        e.preventDefault();
        self.constrain(row);
        self.render();
        var again = self.svg.querySelector('[data-row-id="' + row.id + '"]');
        if (again) again.focus();
      }
    });

    // Pointer events cover mouse, touch and pen in one path.
    this.svg.addEventListener('pointerdown', function (e) {
      var g = e.target.closest('[data-row-id]');
      if (!g) return;
      var row = self.getRow(g.getAttribute('data-row-id'));
      if (!row) return;
      var p = self.toRoom(e);
      self.drag = { id: row.id, dx: p.x - row.cx, dy: p.y - row.y, moved: false };
      self.state.selectedId = row.id;
      // Capture keeps the drag alive if the finger strays outside the row.
      // Not fatal if it fails — pointermove is bound on the svg either way.
      try { g.setPointerCapture(e.pointerId); } catch (err) {}
      self.root.classList.add('rp--dragging');
      e.preventDefault();
    });

    this.svg.addEventListener('pointermove', function (e) {
      if (!self.drag) return;
      var row = self.getRow(self.drag.id);
      if (!row) return;
      var p = self.toRoom(e);
      row.cx = p.x - self.drag.dx;
      row.y = p.y - self.drag.dy;
      self.drag.moved = true;
      self.constrain(row);
      self.render();
      e.preventDefault();
    });

    function endDrag(e) {
      if (!self.drag) return;
      var row = self.getRow(self.drag.id);
      if (row) { self.snap(row); self.constrain(row); }
      self.drag = null;
      self.root.classList.remove('rp--dragging');
      self.render();
      if (e) e.preventDefault();
    }
    this.svg.addEventListener('pointerup', endDrag);
    this.svg.addEventListener('pointercancel', endDrag);
  };

  /* ---------------------------------------------------------- room I/O -- */
  Planner.prototype.readRoomInputs = function () {
    var s = this.state.room;
    var w = G.parseLength(this.inputs.width.value, 'ft');
    var l = G.parseLength(this.inputs.length.value, 'ft');
    var scr = G.parseLength(this.inputs.screen.value, 'in');
    var ch = this.inputs.ceiling ? G.parseLength(this.inputs.ceiling.value, 'ft') : null;

    if (w) s.widthIn = clamp(w, C.MIN_ROOM_IN, C.MAX_ROOM_IN);
    if (l) s.lengthIn = clamp(l, C.MIN_ROOM_IN, C.MAX_ROOM_IN);
    if (scr) s.screenDiagonalIn = clamp(scr, G.DEFAULTS.MIN_SCREEN_DIAGONAL_IN, G.DEFAULTS.MAX_SCREEN_DIAGONAL_IN);
    if (ch) s.ceilingHeightIn = clamp(ch, 72, 240);

    // Echo the normalised values back so the field always shows what we used.
    this.inputs.width.value = ftc(s.widthIn);
    this.inputs.length.value = ftc(s.lengthIn);
    this.inputs.screen.value = Math.round(s.screenDiagonalIn);
    if (this.inputs.ceiling) this.inputs.ceiling.value = ftc(s.ceilingHeightIn);

    var self = this;
    this.state.rows.forEach(function (r) { self.constrain(r); });
    this.render();
  };

  /* -------------------------------------------------------------- rows -- */
  Planner.prototype.getRow = function (id) {
    return this.state.rows.filter(function (r) { return r.id === id; })[0];
  };

  Planner.prototype.addRow = function (seats) {
    var room = this.state.room;
    var w = rowWidthFor(seats);
    var vd = G.checkViewingDistance(
      { wallClearanceIn: C.ROW_WALL_CLEARANCE_IN },
      { lengthIn: room.lengthIn, screenDiagonalIn: room.screenDiagonalIn }
    );
    // Place the first row at the ideal distance; stack later rows behind it.
    var y = this.state.rows.length === 0
      ? clamp(vd.recommendedIn, C.ROW_DEPTH_RECLINED_IN, room.lengthIn - C.ROW_WALL_CLEARANCE_IN)
      : Math.max.apply(null, this.state.rows.map(function (r) { return r.y; })) + G.DEFAULTS.ROW_SPACING_IDEAL_IN;

    var row = {
      id: this.uid + '-r' + (this.state.rows.length + 1) + '-' + Date.now().toString(36),
      seats: seats,
      widthIn: w,
      depthUprightIn: C.ROW_DEPTH_UPRIGHT_IN,
      depthReclinedIn: C.ROW_DEPTH_RECLINED_IN,
      wallClearanceIn: C.ROW_WALL_CLEARANCE_IN,
      cx: room.widthIn / 2,
      y: y
    };
    this.constrain(row);
    this.state.rows.push(row);
    this.state.selectedId = row.id;
    // A freshly added row should land somewhere valid if the room allows it,
    // rather than being clamped against the back wall and instantly warning.
    this.autoArrange();
    return row;
  };

  /**
   * Re-space every row front-to-back. Called when a row is ADDED only — once
   * the shopper drags something, their placement is left alone.
   *
   * Anchors the front row at the ideal viewing distance, then walks back at
   * `gap`. If that overshoots the back wall, the whole stack slides forward;
   * if the front row would then be pushed through the screen wall, we retry at
   * minimum spacing, and failing that leave it for the warnings to explain.
   */
  Planner.prototype.autoArrange = function () {
    var room = this.state.room;
    var K = G.DEFAULTS;
    var rows = this.state.rows.slice().sort(function (a, b) { return a.y - b.y; });
    if (rows.length < 2) return;

    var maxAllowed = room.lengthIn - C.ROW_WALL_CLEARANCE_IN;
    var vd = G.checkViewingDistance(
      { wallClearanceIn: C.ROW_WALL_CLEARANCE_IN },
      { lengthIn: room.lengthIn, screenDiagonalIn: room.screenDiagonalIn }
    );

    function place(gap) {
      var y0 = clamp(vd.recommendedIn, rows[0].depthReclinedIn, maxAllowed);
      var ys = rows.map(function (_, i) { return y0 + i * gap; });
      var overflow = ys[ys.length - 1] - maxAllowed;
      if (overflow > 0) ys = ys.map(function (y) { return y - overflow; });
      return ys[0] >= rows[0].depthReclinedIn ? ys : null;
    }

    var ys = place(K.ROW_SPACING_IDEAL_IN) || place(K.ROW_SPACING_MIN_IN);
    if (!ys) return;
    rows.forEach(function (r, i) { r.y = ys[i]; });
  };

  Planner.prototype.removeRow = function (id) {
    this.state.rows = this.state.rows.filter(function (r) { return r.id !== id; });
    if (this.state.selectedId === id) this.state.selectedId = null;
  };

  Planner.prototype.reset = function () {
    this.state.rows = [];
    this.addRow(3);
    this.render();
  };

  /** Keep a row inside the room. */
  Planner.prototype.constrain = function (row) {
    var room = this.state.room;
    row.cx = clamp(row.cx, row.widthIn / 2, Math.max(row.widthIn / 2, room.widthIn - row.widthIn / 2));
    row.y = clamp(row.y, row.depthUprightIn, Math.max(row.depthUprightIn, room.lengthIn - row.wallClearanceIn));
  };

  /** Snap to the positions a planner would actually choose. */
  Planner.prototype.snap = function (row) {
    var room = this.state.room;
    var t = C.SNAP_TOLERANCE_IN;

    // Centre on the screen.
    if (Math.abs(row.cx - room.widthIn / 2) < t) row.cx = room.widthIn / 2;

    // Ideal viewing distance.
    var vd = G.checkViewingDistance(row, { lengthIn: room.lengthIn, screenDiagonalIn: room.screenDiagonalIn });
    [vd.idealMinIn, vd.idealMaxIn].forEach(function (target) {
      if (Math.abs(row.y - target) < t) row.y = target;
    });

    // Ideal spacing behind any other row.
    var self = this;
    this.state.rows.forEach(function (other) {
      if (other.id === row.id) return;
      [G.DEFAULTS.ROW_SPACING_IDEAL_IN, G.DEFAULTS.ROW_SPACING_MIN_IN].forEach(function (gap) {
        if (Math.abs(row.y - (other.y + gap)) < t) row.y = other.y + gap;
        if (Math.abs(row.y - (other.y - gap)) < t) row.y = other.y - gap;
      });
      if (Math.abs(row.cx - other.cx) < t) row.cx = other.cx;
    });
    self.constrain(row);
  };

  /* ---------------------------------------------------- pointer → room -- */
  Planner.prototype.toRoom = function (e) {
    var pt = this.svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    var ctm = this.svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    var p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  };

  /* ==================================================== analysis =========
   * Everything the overlay and the summary need, derived from state.
   * ===================================================================== */
  Planner.prototype.analyse = function () {
    var room = this.state.room;
    var rows = this.state.rows.slice().sort(function (a, b) { return a.y - b.y; });
    var warnings = [];
    var K = G.DEFAULTS;

    var vdRef = G.checkViewingDistance(
      { wallClearanceIn: C.ROW_WALL_CLEARANCE_IN },
      { lengthIn: room.lengthIn, screenDiagonalIn: room.screenDiagonalIn }
    );

    rows.forEach(function (row, i) {
      var label = row.seats + '-seat row' + (rows.length > 1 ? ' (' + (i === 0 ? 'front' : i === rows.length - 1 ? 'back' : 'middle') + ')' : '');
      row._label = label;
      row._issues = [];

      // Width against the room.
      var needed = row.widthIn + 2 * K.SIDE_CLEARANCE_IN;
      if (row.widthIn > room.widthIn) {
        row._issues.push('too-wide');
        warnings.push({ level: 'fail', row: label, text: label + ' is ' + ft(row.widthIn) + ' wide — wider than the ' + ft(room.widthIn) + ' room.' });
      } else if (room.widthIn < needed) {
        row._issues.push('tight-width');
        warnings.push({ level: 'warn', row: label, text: label + ' leaves under ' + ft(K.SIDE_CLEARANCE_IN) + ' each side. It fits, but there is no walkway.' });
      }

      // Too close to the screen.
      if (row.y < vdRef.idealMinIn - 0.5) {
        row._issues.push('too-close');
        warnings.push({
          level: row.y < vdRef.idealMinIn * 0.75 ? 'fail' : 'warn',
          row: label,
          text: label + ' sits ' + ft(row.y) + ' from the screen. For a ' + Math.round(room.screenDiagonalIn) +
            '″ screen the comfortable range starts at ' + ft(vdRef.idealMinIn) + '.'
        });
      }

      // Reclined footrest hitting the screen wall.
      if (row.y - row.depthReclinedIn < 0) {
        row._issues.push('recline-front');
        warnings.push({ level: 'fail', row: label, text: label + ' cannot fully recline — the footrest would pass through the screen wall.' });
      }

      // Clearance behind for the mechanism.
      if (row.y + row.wallClearanceIn > room.lengthIn + 0.5) {
        row._issues.push('recline-back');
        warnings.push({ level: 'fail', row: label, text: label + ' needs ' + ft(row.wallClearanceIn) + ' behind it to recline and does not have it.' });
      }

      // Spacing to the row in front.
      if (i > 0) {
        var gap = row.y - rows[i - 1].y;
        if (gap < K.ROW_SPACING_MIN_IN - 0.5) {
          row._issues.push('spacing');
          warnings.push({
            level: 'fail',
            row: label,
            text: 'Only ' + ft(gap) + ' between this row and the one in front. Allow at least ' + ft(K.ROW_SPACING_MIN_IN) + ' back-to-back.'
          });
        } else if (gap < K.ROW_SPACING_IDEAL_IN - 0.5) {
          row._issues.push('spacing-tight');
          warnings.push({
            level: 'warn',
            row: label,
            text: ft(gap) + ' between rows works, but ' + ft(K.ROW_SPACING_IDEAL_IN) + ' is more comfortable.'
          });
        }
      }
    });

    // Riser + sightlines for every row behind the front one.
    var riser = null;
    if (rows.length > 1) {
      var headroom = room.ceilingHeightIn - this.state.riserHeightIn;
      riser = {
        heightIn: this.state.riserHeightIn,
        rowsOnRiser: rows.length - 1,
        headroomIn: headroom,
        headroomOk: headroom >= K.MIN_HEADROOM_IN
      };
      if (!riser.headroomOk) {
        warnings.push({
          level: 'warn',
          row: null,
          text: 'A ' + riser.heightIn + '″ riser under your ' + ft(room.ceilingHeightIn) + ' ceiling leaves ' +
            ft(headroom) + ' of headroom — below the ' + ft(K.MIN_HEADROOM_IN) + ' we recommend.'
        });
      }
    }

    var totalSeats = rows.reduce(function (n, r) { return n + r.seats; }, 0);

    return { rows: rows, warnings: warnings, riser: riser, viewing: vdRef, totalSeats: totalSeats };
  };

  /* ======================================================= render ======== */
  Planner.prototype.render = function () {
    var room = this.state.room;
    var a = this.analyse();
    var svg = this.svg;

    // Margin around the room so wall labels have somewhere to live.
    var M = 46;
    var vbW = room.widthIn + M * 2;
    var vbH = room.lengthIn + M * 2;
    svg.setAttribute('viewBox', -M + ' ' + -M + ' ' + vbW + ' ' + vbH);
    // Drives the CSS max-width cap so the plan never letterboxes.
    svg.style.setProperty('--rp-aspect', (vbW / vbH).toFixed(4));

    // SVG text is measured in user units (inches), so a fixed font-size would
    // look tiny in a huge room and enormous in a small one. Deriving the type
    // scale from the drawing keeps labels the same visual size at any room
    // size and any viewport.
    var U = vbH / 42;
    svg.setAttribute('aria-label',
      'Plan view: ' + ft(room.widthIn) + ' by ' + ft(room.lengthIn) + ' room with ' +
      a.rows.length + ' seating row' + (a.rows.length === 1 ? '' : 's') + ' and ' + a.totalSeats + ' seats.');
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    var defs = el('defs', {}, svg);
    var hatch = el('pattern', { id: this.uid + '-hatch', width: 8, height: 8, patternUnits: 'userSpaceOnUse', patternTransform: 'rotate(45)' }, defs);
    el('line', { x1: 0, y1: 0, x2: 0, y2: 8, stroke: 'var(--rp-rule)', 'stroke-width': 2 }, hatch);

    /* --- viewing distance band ---------------------------------------- */
    var bandTop = Math.min(a.viewing.idealMinIn, room.lengthIn);
    var bandH = Math.max(0, Math.min(a.viewing.idealMaxIn, room.lengthIn) - bandTop);
    if (bandH > 0) {
      el('rect', {
        x: 0, y: bandTop, width: room.widthIn, height: bandH,
        fill: 'var(--rp-band)', 'pointer-events': 'none'
      }, svg);
      // Outside the right wall, so it can never collide with a row.
      var bandLabelX = room.widthIn + U * 1.9;
      var bandLabelY = bandTop + bandH / 2;
      el('text', {
        x: bandLabelX, y: bandLabelY, fill: 'var(--rp-ink-faint)',
        'font-size': U * 0.85, 'text-anchor': 'middle', 'pointer-events': 'none',
        transform: 'rotate(90 ' + bandLabelX + ' ' + bandLabelY + ')'
      }, svg).textContent = 'best seating';
    }

    /* --- room shell ---------------------------------------------------- */
    el('rect', {
      x: 0, y: 0, width: room.widthIn, height: room.lengthIn,
      fill: 'none', stroke: 'var(--rp-ink)', 'stroke-width': 3
    }, svg);

    /* --- screen -------------------------------------------------------- */
    // 16:9 screen width from the diagonal.
    var screenW = room.screenDiagonalIn * (16 / Math.sqrt(16 * 16 + 9 * 9));
    screenW = Math.min(screenW, room.widthIn);
    el('rect', {
      x: (room.widthIn - screenW) / 2, y: -U * 0.8, width: screenW, height: U * 0.8,
      fill: 'var(--rp-accent)'
    }, svg);
    el('text', {
      x: room.widthIn / 2, y: -U * 1.7, fill: 'var(--rp-ink-faint)',
      'font-size': U * 0.9, 'text-anchor': 'middle'
    }, svg).textContent = Math.round(room.screenDiagonalIn) + '″ screen · ' + ftc(screenW) + ' wide';

    /* --- dimension labels ---------------------------------------------- */
    el('text', {
      x: room.widthIn / 2, y: room.lengthIn + U * 2.4, fill: 'var(--rp-ink-soft)',
      'font-size': U, 'text-anchor': 'middle'
    }, svg).textContent = ft(room.widthIn) + ' wide';
    var sideX = -U * 1.9;
    el('text', {
      x: sideX, y: room.lengthIn / 2, fill: 'var(--rp-ink-soft)',
      'font-size': U, 'text-anchor': 'middle',
      transform: 'rotate(-90 ' + sideX + ' ' + room.lengthIn / 2 + ')'
    }, svg).textContent = ft(room.lengthIn) + ' deep';

    /* --- rows ---------------------------------------------------------- */
    var self = this;
    a.rows.forEach(function (row, i) {
      var bad = row._issues.some(function (t) { return t === 'too-wide' || t === 'recline-front' || t === 'recline-back' || t === 'spacing'; });
      var warn = !bad && row._issues.length > 0;
      var tone = bad ? 'var(--rp-fail)' : warn ? 'var(--rp-warn)' : 'var(--rp-pass)';
      var onRiser = i > 0;

      var g = el('g', {
        'data-row-id': row.id,
        class: 'rp-row' + (self.state.selectedId === row.id ? ' rp-row--selected' : ''),
        tabindex: 0,
        role: 'button',
        'aria-label': row._label + ', ' + ft(row.y) + ' from the screen. Arrow keys to move, Delete to remove.'
      }, svg);

      var x = row.cx - row.widthIn / 2;
      var yTop = row.y - row.depthUprightIn;

      // Reclined footprint (ghost).
      el('rect', {
        x: x, y: row.y - row.depthReclinedIn, width: row.widthIn, height: row.depthReclinedIn,
        fill: 'none', stroke: tone, 'stroke-width': 1.5, 'stroke-dasharray': '6 5', opacity: 0.6
      }, g);

      // Riser platform under the row.
      if (onRiser) {
        el('rect', {
          x: x - 8, y: yTop - 8, width: row.widthIn + 16, height: row.depthUprightIn + 16,
          fill: 'url(#' + self.uid + '-hatch)', stroke: 'var(--rp-rule-strong)',
          'stroke-width': 1.5, rx: 4, opacity: 0.85
        }, g);
      }

      // The seats themselves.
      el('rect', {
        x: x, y: yTop, width: row.widthIn, height: row.depthUprightIn,
        fill: 'var(--rp-seat)', stroke: tone, 'stroke-width': 2.5, rx: 5
      }, g);

      // Seat divisions — kept to the back third so they never cross the label.
      for (var s = 1; s < row.seats; s++) {
        var sx = x + (row.widthIn / row.seats) * s;
        el('line', {
          x1: sx, y1: yTop + U * 0.3, x2: sx, y2: yTop + row.depthUprightIn * 0.36,
          stroke: tone, 'stroke-width': 1.25, opacity: 0.55
        }, g);
      }

      // Which way it faces — a notch along the front (screen-facing) edge.
      el('line', {
        x1: x + U * 0.8, y1: yTop + U * 0.45, x2: x + row.widthIn - U * 0.8, y2: yTop + U * 0.45,
        stroke: tone, 'stroke-width': 2, opacity: 0.4
      }, g);

      // Both labels sit INSIDE the seat rectangle. Anything drawn outside it
      // collides with the row behind as soon as the rows are close together.
      var midY = yTop + row.depthUprightIn / 2;
      el('text', {
        x: row.cx, y: midY - U * 0.15,
        'text-anchor': 'middle', 'font-size': U, fill: 'var(--rp-ink)',
        'pointer-events': 'none'
      }, g).textContent = row.seats + ' seats' + (onRiser ? ' · riser' : '');

      el('text', {
        x: row.cx, y: midY + U * 1.05, 'text-anchor': 'middle', 'font-size': U * 0.8,
        fill: 'var(--rp-ink-faint)', 'pointer-events': 'none'
      }, g).textContent = ftc(row.y) + ' out';
    });

    this.renderPanels(a);
  };

  Planner.prototype.renderPanels = function (a) {
    /* --- row list ------------------------------------------------------ */
    if (this.rowListEl) {
      this.rowListEl.innerHTML = a.rows.length
        ? a.rows.map(function (r, i) {
            return '<li class="rp-rowitem">' +
              '<span class="rp-rowitem__name">' + esc(r._label) + '</span>' +
              '<span class="rp-rowitem__meta">' + esc(ftc(r.widthIn)) + ' · ' + esc(ftc(r.y)) + ' out</span>' +
              '<button type="button" class="rp-rowitem__remove" data-rp-remove="' + esc(r.id) + '" ' +
              'aria-label="Remove ' + esc(r._label) + '">Remove</button></li>';
          }).join('')
        : '<li class="rp-rowitem rp-rowitem--empty">No rows yet — add one above.</li>';
    }

    /* --- warnings ------------------------------------------------------ */
    var w = a.warnings;
    var html;
    if (!a.rows.length) {
      html = '<p class="rp-clear">Add a row to start planning.</p>';
    } else if (!w.length) {
      html = '<p class="rp-clear"><span class="rp-clear__mark" aria-hidden="true">' + checkIcon() + '</span>' +
        'This layout works. ' + a.totalSeats + ' seats, everything within the clearances we plan to.</p>';
    } else {
      html = '<ul class="rp-warnings__list">' + w.map(function (x) {
        return '<li class="rp-warning rp-warning--' + x.level + '">' +
          '<span class="rp-warning__icon" aria-hidden="true">' + (x.level === 'fail' ? crossIcon() : alertIcon()) + '</span>' +
          '<span>' + esc(x.text) + '</span></li>';
      }).join('') + '</ul>';
    }
    this.warningsEl.innerHTML = html;

    /* --- summary ------------------------------------------------------- */
    var room = this.state.room;
    var rows = [
      ['Room', ft(room.widthIn) + ' × ' + ft(room.lengthIn)],
      ['Screen', Math.round(room.screenDiagonalIn) + '″ diagonal'],
      ['Seating', a.rows.length + ' row' + (a.rows.length === 1 ? '' : 's') + ' · ' + a.totalSeats + ' seats'],
      ['Best viewing', ft(a.viewing.idealMinIn) + ' – ' + ft(a.viewing.idealMaxIn)]
    ];
    if (a.riser) {
      rows.push(['Riser', a.riser.heightIn + '″ under ' + a.riser.rowsOnRiser + ' row' + (a.riser.rowsOnRiser === 1 ? '' : 's')]);
      rows.push(['Headroom', ft(a.riser.headroomIn) + (a.riser.headroomOk ? '' : ' — low')]);
    }
    this.summaryEl.innerHTML = rows.map(function (r) {
      return '<div class="rp-figure"><dt>' + esc(r[0]) + '</dt><dd>' + esc(r[1]) + '</dd></div>';
    }).join('');
  };

  function svgIcon(path) {
    return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + path + '</svg>';
  }
  function checkIcon() { return svgIcon('<path d="M20 6 9 17l-5-5"/>'); }
  function alertIcon() { return svgIcon('<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>'); }
  function crossIcon() { return svgIcon('<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>'); }

  /* ======================================================== email ======== */
  Planner.prototype.emailLayout = function () {
    var a = this.analyse();
    var room = this.state.room;
    var L = [];
    L.push('My room layout from the Home Cinema Supply planner');
    L.push('');
    L.push('ROOM');
    L.push('  Width:   ' + ft(room.widthIn));
    L.push('  Length:  ' + ft(room.lengthIn));
    L.push('  Ceiling: ' + ft(room.ceilingHeightIn));
    L.push('  Screen:  ' + Math.round(room.screenDiagonalIn) + '" diagonal');
    L.push('');
    L.push('SEATING (' + a.totalSeats + ' seats in ' + a.rows.length + ' row' + (a.rows.length === 1 ? '' : 's') + ')');
    a.rows.forEach(function (r, i) {
      L.push('  ' + (i + 1) + '. ' + r.seats + '-seat row, ' + ft(r.widthIn) + ' wide');
      L.push('       ' + ft(r.y) + ' from the screen wall');
      if (i > 0) L.push('       ' + ft(r.y - a.rows[i - 1].y) + ' behind the row in front');
    });
    L.push('');
    L.push('RECOMMENDATIONS');
    L.push('  Best viewing distance: ' + ft(a.viewing.idealMinIn) + ' to ' + ft(a.viewing.idealMaxIn));
    if (a.riser) {
      L.push('  Riser: ' + a.riser.heightIn + '" under ' + a.riser.rowsOnRiser + ' row(s)');
      L.push('  Headroom over riser: ' + ft(a.riser.headroomIn));
    } else {
      L.push('  Riser: not needed for a single row');
    }
    L.push('');
    if (a.warnings.length) {
      L.push('THINGS TO CHECK');
      a.warnings.forEach(function (w) { L.push('  - ' + w.text); });
    } else {
      L.push('No clearance problems found in this layout.');
    }
    L.push('');
    L.push('Figures are planning guidance based on standard home-theater');
    L.push('practice, not manufacturer certification.');

    var to = this.root.getAttribute('data-planner-email') || '';
    var subject = 'My room layout — ' + ft(room.widthIn) + ' x ' + ft(room.lengthIn);
    var href = 'mailto:' + encodeURIComponent(to) +
      '?subject=' + encodeURIComponent(subject) +
      '&body=' + encodeURIComponent(L.join('\n'));

    // mailto URLs get truncated past ~2000 chars in some clients; ours is far
    // shorter, but guard anyway.
    if (href.length > 1900) href = href.slice(0, 1900);
    window.location.href = href;
  };

  /* ========================================================= boot ======== */
  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-room-planner]'), function (root) {
      if (root.dataset.rpBooted) return;
      root.dataset.rpBooted = '1';
      try {
        new Planner(root);
        root.classList.add('rp--ready');
      } catch (e) {
        // Leave the static fallback in place rather than a half-built planner.
        if (window.console && console.error) console.error('[room-planner]', e);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
